import PropTypes from 'prop-types';
import { Component } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MIN_PASSWORD_REQUIREMENTS_TEXT } from '~/helpers/constants';
import decodeURIComponentSafe from '~/helpers/decodeURIComponentSafe';
import { routerLocationShape } from '~/helpers/propTypeShapes';
import validateEmail from '~/helpers/validation/validateEmail';
import '~/style/login.css';
import '~/style/resetpassword.css';

export default class ResetPassword extends Component {
  constructor(props) {
    super(props);
    this.state = {
      email: '',
      password: '',
      passwordConfirmation: '',
      requestFormValidationState: 'invalid',
      requestFormSubmitted: false,
      resetFormValidationState: 'invalid',
      resetFormSubmitted: false,
    };

    this.handleEmailChange = this.handleEmailChange.bind(this);
    this.handlePasswordChange = this.handlePasswordChange.bind(this);
    this.handlePasswordConfirmationChange = this.handlePasswordConfirmationChange.bind(this);
    this.handleRequestSubmit = this.handleRequestSubmit.bind(this);
    this.handleResetSubmit = this.handleResetSubmit.bind(this);
  }

  componentDidMount() {
    this.props.showHeader(false);
  }

  componentWillUnmount() {
    this.props.showHeader(true);
  }

  handleEmailChange(event) {
    this.setState({ email: event.target.value }, () => {
      this.checkRequestFormValidity();
    });
  }

  handlePasswordChange(event) {
    this.setState({ password: event.target.value }, () => {
      this.checkResetFormValidity();
    });
  }

  handlePasswordConfirmationChange(event) {
    this.setState({ passwordConfirmation: event.target.value }, () => {
      this.checkResetFormValidity();
    });
  }

  handleRequestSubmit(event) {
    const formData = `email=${encodeURIComponent(this.state.email)}`;
    const params = {
      method: 'post',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-API-VERSION': '2.0',
      },
      body: formData,
    };

    event.preventDefault();
    fetch(`${this.props.APIBaseURL}/password/reset`, params)
      .then((res) => {
        // per discussion with Ben D, treat any sub-500 status as success
        if (res.status < 500) {
          this.setState({ requestFormSubmitted: true });
        } else {
          throw new Error();
        }
      })
      .catch(() => {
        toast.error('Password reset failed, please try again.');
      });
  }

  handleResetSubmit(event) {
    const queryObject = this.parseQuery(this.props.location.search);
    const formData = {
      token: queryObject.token,
      userID: queryObject.id,
      password: this.state.password,
    };
    const params = {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        'X-API-VERSION': '2.0',
      },
      body: JSON.stringify(formData),
    };

    event.preventDefault();
    fetch(`${this.props.APIBaseURL}/password/update-from-token`, params)
      .then((response) => {
        function chainError(error) {
          throw new Error(error);
        }

        if (response.status === 404) {
          toast.error('This email link has expired. Please initiate password reset from the web portal.');
        } else if (response.status === 400) {
          this.props.trackEvent('400', {
            source: 'handleRequestSubmit',
          });
          response
            .text()
            .then((errorMessage) => {
              toast.error(errorMessage, { autoClose: 30000 });
              chainError(errorMessage);
            })
            .catch((error) => {
              // eslint-disable-next-line no-console
              console.error('password/update API call failed. ', error);
            });
        } else {
          this.setState({ resetFormSubmitted: true });
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('password/update API call failed. ', error);
      });
  }

  checkRequestFormValidity() {
    const isValidEmail = validateEmail(this.state.email);

    if (isValidEmail) {
      this.setState({ requestFormValidationState: 'valid' });
    } else {
      this.setState({ requestFormValidationState: 'invalid' });
    }
  }

  checkResetFormValidity() {
    if (this.state.password.length > 7 && this.state.password === this.state.passwordConfirmation) {
      this.setState({ resetFormValidationState: 'valid' });
    } else {
      this.setState({ resetFormValidationState: 'invalid' });
    }
  }

  parseQuery(queryString) {
    const query = {};
    const pairs = (queryString[0] === '?' ? queryString.substr(1) : queryString).split('&');
    for (let i = 0; i < pairs.length; i += 1) {
      const pair = pairs[i].split('=');
      query[decodeURIComponentSafe(pair[0])] = decodeURIComponentSafe(pair[1] || '');
    }
    return query;
  }

  render() {
    let requestDisabled = null;
    let resetDisabled = null;
    let formOrMessage = '';
    const queryObject = this.parseQuery(this.props.location.search);

    if (this.state.requestFormValidationState === 'invalid') {
      requestDisabled = 'disabled';
    }
    if (this.state.resetFormValidationState === 'invalid') {
      resetDisabled = 'disabled';
    }
    if (!this.state.requestFormSubmitted) {
      if (queryObject.token && queryObject.id && !this.state.resetFormSubmitted) {
        formOrMessage = (
          <form id="passwordResetForm" onSubmit={this.handleResetSubmit}>
            <fieldset>
              <label className="text-input-label" htmlFor="resetPassword">
                PASSWORD
                <input type="password" id="resetPassword" onChange={this.handlePasswordChange} />
              </label>
              <label className="text-input-label" htmlFor="passwordConfirmation">
                PASSWORD CONFIRMATION
                <input type="password" id="passwordConfirmation" onChange={this.handlePasswordConfirmationChange} />
              </label>
              <h3 className="min-requirements">
                <br />
                {MIN_PASSWORD_REQUIREMENTS_TEXT}
              </h3>
              <div className="submit-container">
                <button
                  disabled={resetDisabled}
                  className={this.state.resetFormValidationState}
                  type="submit"
                  id="resetSubmitButton"
                  value=""
                >
                  <span className="submit-text">Reset password</span>
                </button>
              </div>
            </fieldset>
          </form>
        );
      } else if (this.state.resetFormSubmitted) {
        formOrMessage = (
          <div className="success-message">
            <p>Your password has been changed. Please click on the link below to log in.</p>
          </div>
        );
      } else {
        formOrMessage = (
          <form id="passwordResetRequestForm" onSubmit={this.handleRequestSubmit}>
            <fieldset>
              <label className="text-input-label" htmlFor="email">
                EMAIL ADDRESS
                <input type="email" id="requestEmail" onChange={this.handleEmailChange} />
              </label>
              <div className="submit-container">
                <button
                  disabled={requestDisabled}
                  className={this.state.requestFormValidationState}
                  type="submit"
                  id="requestSubmitButton"
                  value=""
                >
                  <span className="submit-text">Request password reset</span>
                </button>
              </div>
            </fieldset>
          </form>
        );
      }
    } else {
      formOrMessage = (
        <div className="success-message">
          <p>
            If you have an account registered under the email address you submitted, you will receive an email with a
            link to instructions for resetting your password.
            <br />
            Thank you.
          </p>
        </div>
      );
    }
    return (
      <div className="login-content content-container">
        <div className="login-left">
          <div className="login-block-type">
            Harness the power
            <br />
            of live video
          </div>
        </div>
        <div className="login-right">
          <div className="login-right-content">
            <div className="login-logo-container">
              <img className="login-logo" src="/sl_logo_name_v2_light.png" alt="Socialive Logo" />
            </div>
            <h2 className="reset-header">Reset your password</h2>
            {formOrMessage}
            <div className="back-to-login">
              <Link to="/login">If you know your password, click here to log in.</Link>
            </div>
            <div className="copyright">
              <span className="byline">
                Copyright © 2007 -{this.props.currentYear} Socialive. All rights reserved. |{' '}
              </span>
              <a
                href="https://www.socialive.us/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="byline"
              >
                Privacy
              </a>
              <span className="byline"> | </span>
              <a
                href="https://www.socialive.us/terms-of-service/"
                target="_blank"
                rel="noopener noreferrer"
                className="byline"
              >
                Terms
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ResetPassword.propTypes = {
  APIBaseURL: PropTypes.string.isRequired,
  currentYear: PropTypes.number.isRequired,
  location: routerLocationShape.isRequired,
  showHeader: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
};
